import { useEffect } from "react";

import { useDispatch } from "react-redux";

import { Card } from "@/components/ui/card";
import { showError, showSuccess } from "@/utils/notification";
import { generateNumericalId } from "@/utils/string";

import type { ReportGeneratorTemplateWithTypeSchema } from "@api/generated/types.gen";

import { useAppForm } from "@components/Form";
import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Input } from "@components/ui/input";

import { useUpdateTemplate } from "@hooks/mutations/use-templates";

import { STATE_OPTIONS } from "../constants/states";
import { useTemplateSections } from "../hooks/useTemplateSectionManager";
import { templateFormSchema } from "../schemas/templateFormSchema";
import type { Section } from "../types/template";
import { AvailableModules } from "./AvailableModules";
import { CreateTemplateStepHeader } from "./CreateTemplateStepHeader";
import { SelectedModulesPanel } from "./SelectedModulesPanel";

type EditTemplateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  template: ReportGeneratorTemplateWithTypeSchema;
  templates: ReportGeneratorTemplateWithTypeSchema[];
};

export const EditTemplateModal = ({
  isOpen,
  onClose,
  template,
  templates,
}: EditTemplateModalProps) => {
  const dispatch = useDispatch();
  const sectionManager = useTemplateSections();

  // Initialize form with template data
  useEffect(() => {
    if (isOpen && template) {
      // Find the base template to get the correct section IDs
      const baseTemplate = template.base_template_id
        ? templates.find((t) => t.id === template.base_template_id)
        : templates.find((t) => !t.is_custom);

      if (baseTemplate?.sections) {
        // Load available sections from base template
        sectionManager.setAvailableSections(
          baseTemplate.sections.map((section, index) => ({
            id: section.id,
            name: section.name,
            order: index,
          }))
        );

        // Map custom template sections to base template sections by name
        // This ensures we send the correct base template section IDs when updating
        const initialSections =
          template.sections
            ?.map((customSection) => {
              const baseSection = baseTemplate.sections.find(
                (bs) => bs.name === customSection.name
              );
              return baseSection
                ? {
                    id: baseSection.id, // Use base template section ID
                    name: baseSection.name,
                    order: customSection.order,
                  }
                : null;
            })
            .filter((section): section is Section => section !== null)
            .sort((a, b) => a.order - b.order) || [];

        sectionManager.setSelectedSections(initialSections);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, template, templates]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      sectionManager.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const updateTemplate = useUpdateTemplate({
    onSuccess: () => {
      showSuccess("Template updated successfully!", dispatch);
      onClose();
    },
    onError: (error) => {
      console.error("Failed to update template:", error);
      showError("Failed to update template. Please try again.", dispatch);
    },
  });

  const form = useAppForm({
    defaultValues: {
      name: template.name,
      baseTemplateId: template.base_template_id || "",
      state: template.state || "",
      description: template.description || "",
    },
    validators: {
      onSubmit: ({ value }) => {
        const result = templateFormSchema.safeParse(value);
        const errors: Record<string, string> = {};

        if (!result.success) {
          result.error.issues.forEach((issue) => {
            if (issue.path[0]) {
              errors[issue.path[0] as string] = issue.message;
            }
          });
        }

        // Check section requirement
        if (sectionManager.selectedSections.length === 0) {
          return {
            form: "Please select at least one section for the template",
            fields: errors,
          };
        }

        // Return field errors if validation failed
        if (!result.success) {
          return { form: undefined, fields: errors };
        }

        return undefined;
      },
    },
    onSubmit: async ({ value }) => {
      if (sectionManager.selectedSections.length === 0) return;

      // If base template is selected, update available sections
      if (value.baseTemplateId) {
        const baseTemplate = templates.find((t) => t.id === value.baseTemplateId);
        if (baseTemplate?.sections) {
          sectionManager.setAvailableSections(
            baseTemplate.sections.map((section, index) => ({
              id: section.id,
              name: section.name,
              order: index,
            }))
          );
        }
      }

      await updateTemplate.mutateAsync({
        templateId: template.id,
        templateData: {
          name: value.name,
          state: value.state,
          description: value.description || null,
          base_template_id: value.baseTemplateId || null,
          selected_sections: sectionManager.selectedSections.map(
            (section, index) => ({
              section_id: section.id,
              order: index,
            })
          ),
        },
      });
    },
  });

  // Update form values when template changes or modal opens
  useEffect(() => {
    if (isOpen && template) {
      form.setFieldValue("name", template.name);
      form.setFieldValue("baseTemplateId", template.base_template_id || "");
      form.setFieldValue("state", template.state || "");
      form.setFieldValue("description", template.description || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, template]);

  const templateOptions = templates
    .filter((t) => !t.is_custom && t.id !== template.id)
    .map((t) => ({
      value: t.id,
      label: t.name,
    }));

  const numericalId = generateNumericalId(template.id);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[90vh] min-w-[960px] max-w-5xl flex-col space-y-2 overflow-hidden rounded-2xl bg-background px-8 py-10">
        <DialogHeader>
          <DialogTitle>Edit : {template.name}</DialogTitle>
        </DialogHeader>

        <form.AppForm>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit(e);
            }}
            className="flex flex-1 flex-col overflow-hidden"
          >
            <div className="flex-1 space-y-6 overflow-y-auto">
              {/* Template Details Section */}
              <Card className="p-4">
                <div className="space-y-6 pt-4">
                  <CreateTemplateStepHeader
                    step={1}
                    totalSteps={2}
                    title="Template Details"
                    description="Paragraph of heading"
                  />

                  <div className="grid grid-cols-4 gap-4">
                    <div className="space-y-1.5">
                      <label
                        htmlFor="edit-template-numerical-id"
                        className="text-sm font-semibold"
                      >
                        Template ID
                      </label>
                      <Input
                        id="edit-template-numerical-id"
                        value={numericalId}
                        readOnly
                        disabled
                      />
                    </div>

                    <form.AppField name="name">
                      {(field) => (
                        <field.InputField
                          label="Template Name"
                          placeholder="Enter template name"
                          required
                        />
                      )}
                    </form.AppField>

                    <form.AppField name="baseTemplateId">
                      {(field) => (
                        <field.SelectField
                          label="Base Template"
                          placeholder="Select base template"
                          options={templateOptions}
                        />
                      )}
                    </form.AppField>

                    <form.AppField name="state">
                      {(field) => (
                        <field.SelectField
                          label="State"
                          placeholder="Select state"
                          options={STATE_OPTIONS}
                          required
                        />
                      )}
                    </form.AppField>
                  </div>

                  <form.AppField name="description">
                    {(field) => (
                      <field.TextAreaField
                        label="Description"
                        placeholder="Description"
                        rows={3}
                      />
                    )}
                  </form.AppField>
                </div>
              </Card>

              {/* Select & Order Modules Section */}
              <Card className="p-4">
                <div className="space-y-6 pt-4">
                  <CreateTemplateStepHeader
                    step={2}
                    totalSteps={2}
                    title="Select & Order Modules"
                    description="Select the modules you want to include in your custom template, then drag them to reorder as needed."
                  />

                  <div className="grid grid-cols-2 gap-6">
                    <AvailableModules
                      unselectedSections={sectionManager.unselectedSections}
                      searchQuery={sectionManager.searchQuery}
                      setSearchQuery={sectionManager.setSearchQuery}
                      selectAllChecked={sectionManager.selectAllChecked}
                      checkedSections={sectionManager.checkedSections}
                      onSelectAll={sectionManager.handleSelectAll}
                      onCheckSection={sectionManager.handleCheckSection}
                      onAddSelected={sectionManager.handleAddSelected}
                    />

                    <SelectedModulesPanel
                      selectedSections={sectionManager.selectedSections}
                      totalAvailable={
                        sectionManager.selectedSections.length +
                        sectionManager.unselectedSections.length
                      }
                      draggedIndex={sectionManager.draggedIndex}
                      dragOverIndex={sectionManager.dragOverIndex}
                      onRemove={sectionManager.handleRemoveSection}
                      onDragStart={sectionManager.handleDragStart}
                      onDragEnd={sectionManager.handleDragEnd}
                      onDragOver={sectionManager.handleDragOver}
                      onDrop={sectionManager.handleDrop}
                    />
                  </div>
                </div>
              </Card>
            </div>

            {/* Footer */}
            <div className="mt-6 flex justify-end gap-3 border-t pt-6">
              <Button type="button" variant="simple-outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  sectionManager.selectedSections.length === 0 ||
                  updateTemplate.isPending
                }
              >
                {updateTemplate.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
};
